// Client-side "Download originals" bundler. Replaces the server ZIP
// endpoint so the proof PNGs included in the bundle are byte-identical
// to what the admin sees on screen (rendered via the same Fabric.js
// toDataURL pipeline). JSZip assembles the .zip locally; the file IDs
// for the originals come straight from the order detail payload the
// page already has loaded.
//
// Layout:
//   ORDER.txt                              — summary header
//   proof/<originalName>                   — legacy single proof file (if any)
//   proofs/<side>.png                      — full A3 transparent proof per garment side
//   proofs/sheet-N - <label>.png           — full DTF sheet proof per sheet
//   artwork/<location>/<originalName>      — garment originals grouped by side
//   sheet-N - <label>/<originalName>       — DTF originals grouped by sheet
import JSZip from 'jszip'
import { renderProofAt300DPI, renderDtfSheetProof } from '../components/admin/MockupCanvases'

export interface BundleOrderInfo {
  number: number | string
  status: string
  createdAt: string | null
}

export interface BundleGarmentSide {
  name: string
  canvas: object | null
  printZone: { left: number; top: number; width: number; height: number }
  printWidthMm: number
}

export interface BundleDtfSheet {
  label: string
  index: number
  canvas: object | null
  lengthCm: number | null
}

export interface BundleProductionFile {
  originalName: string | null
  url: string                           // signed /api/files/... URL
  location?: string | null              // garment placements
  sheetIndex?: number | null            // DTF placements
  sheetLabel?: string | null            // DTF placements
}

export interface BundleArgs {
  order: BundleOrderInfo
  designKind: 'garment' | 'dtf' | string
  proofUrl?: string | null              // legacy combined proof, if any
  proofOriginalName?: string | null
  garmentSides?: BundleGarmentSide[]    // present for garment designs
  dtfSheets?: BundleDtfSheet[]          // present for DTF designs
  dtfProofBg?: 'white' | 'grey' | 'black'  // DTF proof substrate (defaults white)
  files: BundleProductionFile[]
  apiSrc: (path: string | null) => string
  onProgress?: (msg: string) => void
}

// Sanitise a label/name for use as a ZIP path segment. Mirrors the
// safeSeg() helper from the previous server endpoint so the layout stays
// consistent on extract.
function safeSeg(input: string | null | undefined, fallback = 'item'): string {
  const s = (input ?? '')
    .toString()
    .replace(/[<>:"|?*\\/]+/g, '')
    .replace(/\s+/g, ' ')
    .trim()
  return s.length ? s : fallback
}

// Strip the `data:image/png;base64,` prefix off a dataURL and return the
// raw bytes. JSZip wants either a base64 string with `{ base64: true }`
// or a Blob/Uint8Array.
function dataUrlToUint8Array(dataUrl: string): Uint8Array {
  const m = dataUrl.match(/^data:[^;]+;base64,(.*)$/)
  if (!m) throw new Error('Unexpected dataURL format')
  const bin = atob(m[1]!)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

async function fetchAsArrayBuffer(url: string): Promise<ArrayBuffer> {
  const r = await fetch(url, { credentials: 'include' })
  if (!r.ok) throw new Error(`fetch failed (${r.status}) for ${url}`)
  return await r.arrayBuffer()
}

export async function bundleOrderZip(args: BundleArgs): Promise<Blob> {
  const { order, designKind, proofUrl, proofOriginalName, garmentSides, dtfSheets, dtfProofBg, files, apiSrc, onProgress } = args
  const zip = new JSZip()

  // ── Manifest ───────────────────────────────────────────────────────────
  const proofCount = designKind === 'garment'
    ? (garmentSides ?? []).filter((s) => s.canvas).length
    : (dtfSheets   ?? []).filter((s) => s.canvas).length
  const headerLines = [
    `Order:           #${order.number}`,
    `Status:          ${order.status}`,
    `Created:         ${order.createdAt ?? ''}`,
    `Design kind:     ${designKind}`,
    `Proofs included: ${proofCount}`,
    `Originals:       ${files.length} file${files.length === 1 ? '' : 's'}`,
    '',
    'Bundle generated by EVRST Prints admin (client-side).',
  ]
  zip.file('ORDER.txt', headerLines.join('\n'))

  // ── Per-piece full-size proofs ────────────────────────────────────────
  if (designKind === 'garment' && garmentSides && garmentSides.length) {
    for (const side of garmentSides) {
      if (!side.canvas) continue
      onProgress?.(`Rendering proof: ${side.name}…`)
      try {
        const { url } = await renderProofAt300DPI(side, side.printWidthMm)
        const bytes = dataUrlToUint8Array(url)
        zip.file(`proofs/${safeSeg(side.name, 'side')}.png`, bytes)
      } catch (e) {
        // Don't abort the whole bundle for one render failure — drop a
        // marker text file so production sees something is missing.
        zip.file(`proofs/${safeSeg(side.name, 'side')}.ERROR.txt`, String((e as Error).message))
      }
    }
  }
  if (designKind === 'dtf' && dtfSheets && dtfSheets.length) {
    const bg = dtfProofBg ?? 'white'
    for (const sheet of dtfSheets) {
      if (!sheet.canvas) continue
      onProgress?.(`Rendering proof: ${sheet.label}…`)
      try {
        const { url } = await renderDtfSheetProof(sheet, bg)
        const bytes = dataUrlToUint8Array(url)
        zip.file(`proofs/sheet-${sheet.index + 1} - ${safeSeg(sheet.label, 'sheet')}.png`, bytes)
      } catch (e) {
        zip.file(`proofs/sheet-${sheet.index + 1} - ${safeSeg(sheet.label, 'sheet')}.ERROR.txt`, String((e as Error).message))
      }
    }
  }

  // ── Legacy combined proof ──────────────────────────────────────────────
  if (proofUrl) {
    onProgress?.('Fetching proof file…')
    try {
      const buf = await fetchAsArrayBuffer(apiSrc(proofUrl))
      zip.file(`proof/${safeSeg(proofOriginalName ?? 'proof.png', 'proof.png')}`, buf)
    } catch {
      // Soft fail — proof file might have been deleted; the per-side
      // proofs we just baked above are the canonical ones anyway.
    }
  }

  // ── Originals ──────────────────────────────────────────────────────────
  for (const f of files) {
    onProgress?.(`Fetching ${f.originalName ?? 'artwork'}…`)
    let folder: string
    if (designKind === 'garment') {
      const loc = safeSeg(f.location?.replace(/_/g, ' ') ?? 'artwork', 'artwork')
      folder = `artwork/${loc}`
    } else {
      const idx = (f.sheetIndex ?? 0) + 1
      const label = safeSeg(f.sheetLabel ?? `Sheet ${idx}`, `sheet ${idx}`)
      folder = `sheet-${idx} - ${label}`
    }
    try {
      const buf = await fetchAsArrayBuffer(apiSrc(f.url))
      zip.file(`${folder}/${safeSeg(f.originalName, 'file')}`, buf)
    } catch (e) {
      zip.file(`${folder}/${safeSeg(f.originalName, 'file')}.ERROR.txt`, String((e as Error).message))
    }
  }

  onProgress?.('Compressing…')
  return await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } })
}

// Helper that wires the bundle into a save-as click for the admin's
// browser. Triggers the download then revokes the blob URL.
export function saveBlobAs(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a); a.click(); document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
